import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Flag } from './Flag';
import { COUNTRIES, findCountry, normalise } from '../lib/countries';

export const NationalitySelect = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = findCountry(value);

  const results = useMemo(() => {
    const q = normalise(query);
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => normalise(c.name).includes(q) || c.code.toLowerCase().includes(q));
  }, [query]);

  const select = (country) => {
    onChange(country);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-12 w-full flex items-center justify-between gap-2 px-3 bg-black/50 border border-white/20 rounded-lg text-left hover:border-white/40 transition-colors"
          data-testid="profile-nationality-select"
        >
          <span className={`flex items-center gap-2 truncate ${selected ? 'text-white' : 'text-[#A1A1AA]'}`}>
            {selected ? (
              <>
                <Flag code={selected.code} emoji={selected.flag} size={22} />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown className="w-4 h-4 text-[#A1A1AA] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0 bg-[#121212] border-white/20"
      >
        <div className="p-2 border-b border-white/10 flex items-center gap-2">
          <Search className="w-4 h-4 text-[#A1A1AA] flex-shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar país..."
            className="h-8 bg-transparent border-0 px-0 focus-visible:ring-0 text-sm"
            data-testid="nationality-search-input"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {results.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] px-3 py-4 text-center">Sem resultados</p>
          ) : (
            results.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => select(country)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-white/10 hover:text-[#32CD32] transition-colors"
                data-testid={`nationality-option-${country.code}`}
              >
                <Flag code={country.code} emoji={country.flag} size={22} />
                <span className="flex-1 truncate">{country.name}</span>
                {value === country.code && <Check className="w-4 h-4 text-[#32CD32]" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NationalitySelect;
